import { Injectable, inject, untracked, type Provider } from '@angular/core';

import { ok, type ApiResult } from '../../../core/models/api-result';
import { HubInterestsService } from '../create/hub-interests.service';
import type { ExploreHub, HubKindWire } from '../explore/hub-explore.vm';
import { HubExploreService, type ExploreFacets, type ExplorePage, type ExploreQuery } from './hub-explore.service';

/**
 * EXPLORE, WITHOUT A SESSION — for `/preview/hubs?explore=1`.
 *
 * `exploreHubs` is viewer-scoped, so unauthenticated it 401s and Explore
 * would be an error banner. This answers the same three wire calls from an
 * in-memory tenant (`explore/hub-explore-preview.corpus.ts`) through the real
 * service's feeds, sequencing and optimistic joins — only the seams differ.
 *
 * ⚠ LAZY, for the reason every preview service here gives: `app.routes.ts`
 * imports this PROVIDER as a value, so anything imported at the top level
 * lands in the eager bundle for every visitor. The corpus, the fixtures and
 * the query engine are imported inside the methods that need them.
 *
 * A short delay on each answer, so the loading states are reviewable too.
 */
@Injectable()
export class HubExplorePreviewService extends HubExploreService {
  private readonly interests = inject(HubInterestsService);
  private corpus: Promise<ExploreHub[]> | null = null;
  private readonly joinedHere = new Set<string>();

  private tenant(): Promise<ExploreHub[]> {
    this.corpus ??= Promise.all([
      import('./hubs-preview.fixtures'),
      import('../explore/hub-explore-preview.corpus'),
    ]).then(([fixtures, corpus]) => corpus.exploreCorpus(fixtures.HUBS_PREVIEW_ITEMS));
    return this.corpus;
  }

  private async catalogue() {
    await this.interests.load();
    return untracked(() => this.interests.groups());
  }

  protected override async fetchPage(
    query: ExploreQuery,
    first: number,
    after: string | null,
  ): Promise<ApiResult<ExplorePage | null>> {
    const [hubs, catalogue, local] = await Promise.all([
      this.tenant(),
      this.catalogue(),
      import('../explore/hub-explore-local'),
    ]);
    await new Promise(resolve => setTimeout(resolve, 180));
    return ok(local.exploreLocally(hubs, query, first, after, this.joinedHere, catalogue));
  }

  protected override async fetchFacets(
    search: string | null,
    kind: HubKindWire | null,
  ): Promise<ApiResult<ExploreFacets | null>> {
    const [hubs, catalogue, local] = await Promise.all([
      this.tenant(),
      this.catalogue(),
      import('../explore/hub-explore-local'),
    ]);
    return ok(local.exploreFacetsLocally(hubs, search, kind, catalogue));
  }

  /** A join changes the harness's copy only. */
  protected override async sendJoin(hubId: string): Promise<ApiResult<unknown>> {
    await new Promise(resolve => setTimeout(resolve, 250));
    this.joinedHere.add(hubId);
    return ok(true);
  }
}

export const HUB_EXPLORE_PREVIEW_PROVIDER: Provider = {
  provide: HubExploreService,
  useClass: HubExplorePreviewService,
};
